import React, { useContext, useEffect, useRef, useState } from 'react';

import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const errorMessage: string = 'Something went wrong!';

interface Props {
    url: string;
    parserFunction: (...args: any[]) => {};
    method: 'post' | 'get' | 'put' | 'delete';
    queryKey: any[];
    onComplete?: Function;
    onError?: Function;
    variables?: any;
    queryConfig?: {
        enabled?: boolean;
    };
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
    count: number;
}

function useFetchLandscapeGraphTableHandler({
    url,
    parserFunction,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
}: Props): outputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);
    const rawDataRef = useRef<any[]>([]);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //initializing states
            setError(null);
            setLoading(true);
            setData(null);
            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            if (variables?.offset === 0) rawDataRef.current = [];

            rawDataRef.current = [...rawDataRef.current, ...(payload?.data || [])];
            const parsedData = parserFunction(rawDataRef.current);
            setCount(payload?.total_count || 0);
            setData(parsedData);
        } catch (error) {
            onFetchError(error);
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        rawDataRef.current = [];
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
    };
    return outputData;
}

export { useFetchLandscapeGraphTableHandler };
